'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { X, CheckCircle, Loader2, ArrowRight, ChevronDown, Brush, Shirt, Camera, Sparkles } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '@/convex/_generated/api';

interface WaitlistFormData {
  name: string;
  jobTitle: string;
  company: string;
  email: string;
  interestedFeature: string;
  interestedFeatureOther: string;
  biggestChallenge: string;
  featureRequest: string;
}

interface WaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const getFeatureOptions = (t: ReturnType<typeof useTranslations>) => [
  {
    id: 'concept-creator',
    label: t('waitlist.features.conceptCreator.label'),
    description: t('waitlist.features.conceptCreator.description'),
    icon: Brush,
  },
  {
    id: 'mix-match',
    label: t('waitlist.features.mixMatch.label'),
    description: t('waitlist.features.mixMatch.description'),
    icon: Shirt,
  },
  {
    id: 'photoshooting',
    label: t('waitlist.features.photoshooting.label'),
    description: t('waitlist.features.photoshooting.description'),
    icon: Camera,
  },
  {
    id: 'other',
    label: t('waitlist.features.other.label'),
    description: t('waitlist.features.other.description'),
    icon: Sparkles,
  },
];

export default function WaitlistModal({ isOpen, onClose }: WaitlistModalProps) {
  const t = useTranslations();
  const addToWaitlist = useMutation(api.waitlist.addToWaitlist);
  const FEATURE_OPTIONS = getFeatureOptions(t);

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<WaitlistFormData>({
    name: '',
    jobTitle: '',
    company: '',
    email: '',
    interestedFeature: '',
    interestedFeatureOther: '',
    biggestChallenge: '',
    featureRequest: '',
  });
  const [showFeedbackSection, setShowFeedbackSection] = useState(false);

  const updateFormData = (field: keyof WaitlistFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) { setError(t('waitlist.errorName')); return; }
    if (!formData.jobTitle.trim()) { setError(t('waitlist.errorJobTitle')); return; }
    if (!formData.company.trim()) { setError(t('waitlist.errorCompany')); return; }
    if (!formData.email.trim()) { setError(t('waitlist.errorEmail')); return; }
    if (!formData.interestedFeature) { setError(t('waitlist.errorFeature')); return; }
    if (formData.interestedFeature === 'other' && !formData.interestedFeatureOther.trim()) {
      setError(t('waitlist.errorFeatureOther')); return;
    }

    setSubmitting(true);
    setError('');

    try {
      await addToWaitlist({
        email: formData.email.trim(),
        name: formData.name.trim(),
        jobTitle: formData.jobTitle.trim(),
        company: formData.company.trim(),
        interestedFeature: formData.interestedFeature,
        interestedFeatureOther: formData.interestedFeatureOther.trim() || undefined,
        biggestChallenge: formData.biggestChallenge.trim() || undefined,
        featureRequest: formData.featureRequest.trim() || undefined,
        source: 'ces-2026',
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t('waitlist.errorFallback'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
      <div
        className="relative w-full max-w-2xl my-8"
        style={{
          animation: 'modalSlideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards',
        }}
      >
        {/* Decorative elements */}
        <div className="absolute -top-4 -left-4 w-24 h-24 border-l-2 border-t-2 border-primary/20 pointer-events-none" />
        <div className="absolute -bottom-4 -right-4 w-24 h-24 border-r-2 border-b-2 border-primary/20 pointer-events-none" />

        <div className="bg-card rounded-sm shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="relative bg-card border-b border-border/50 px-8 py-8 overflow-hidden">
            <div
              className="absolute inset-0 opacity-5"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234f46e5' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-muted-foreground/70 hover:text-muted-foreground transition-colors rounded-full hover:bg-secondary"
              aria-label={t('aria.closeModal')}
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-light tracking-tight text-foreground mb-3" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                {t('waitlist.title')}
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-lg">
                {t('waitlist.subtitle')}
              </p>
            </div>
          </div>

          {success ? (
            <div className="px-8 py-16 text-center">
              <div
                className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-lg shadow-emerald-200"
                style={{ animation: 'successPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}
              >
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-light text-foreground mb-3 tracking-tight">
                {t('waitlist.successTitle')}
              </h3>
              <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                {t('waitlist.successMessage')}
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 bg-primary text-white text-sm font-medium tracking-wide uppercase hover:bg-primary/90 transition-colors"
              >
                {t('waitlist.close')}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-8 py-6">
              <div className="space-y-6">
                {/* Row 1: Name & Job Title */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="wl-name" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      {t('waitlist.nameLabel')} <span className="text-primary">*</span>
                    </label>
                    <input
                      id="wl-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => updateFormData('name', e.target.value)}
                      placeholder={t('waitlist.namePlaceholder')}
                      autoComplete="name"
                      className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-border focus:border-primary focus:ring-0 text-foreground placeholder:text-muted-foreground/50 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="wl-jobTitle" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      {t('waitlist.jobTitleLabel')} <span className="text-primary">*</span>
                    </label>
                    <input
                      id="wl-jobTitle"
                      type="text"
                      value={formData.jobTitle}
                      onChange={(e) => updateFormData('jobTitle', e.target.value)}
                      placeholder={t('waitlist.jobTitlePlaceholder')}
                      autoComplete="organization-title"
                      className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-border focus:border-primary focus:ring-0 text-foreground placeholder:text-muted-foreground/50 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                {/* Row 2: Company & Email */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="wl-company" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      {t('waitlist.companyLabel')} <span className="text-primary">*</span>
                    </label>
                    <input
                      id="wl-company"
                      type="text"
                      value={formData.company}
                      onChange={(e) => updateFormData('company', e.target.value)}
                      placeholder={t('waitlist.companyPlaceholder')}
                      autoComplete="organization"
                      className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-border focus:border-primary focus:ring-0 text-foreground placeholder:text-muted-foreground/50 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="wl-email" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                      {t('waitlist.emailLabel')} <span className="text-primary">*</span>
                    </label>
                    <input
                      id="wl-email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => updateFormData('email', e.target.value)}
                      placeholder={t('waitlist.emailPlaceholder')}
                      autoComplete="email"
                      className="w-full px-0 py-3 bg-transparent border-0 border-b-2 border-border focus:border-primary focus:ring-0 text-foreground placeholder:text-muted-foreground/50 transition-colors"
                      disabled={submitting}
                    />
                  </div>
                </div>

                {/* Feature Interest Selection */}
                <div className="space-y-3 pt-2">
                  <label className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                    {t('waitlist.featureLabel')} <span className="text-primary">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {FEATURE_OPTIONS.map((option) => {
                      const isSelected = formData.interestedFeature === option.id;
                      return (
                        <label
                          key={option.id}
                          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer transition-all text-sm ${
                            isSelected
                              ? 'border-primary bg-primary/10 text-primary font-medium'
                              : 'border-border bg-secondary text-muted-foreground hover:border-border hover:bg-secondary'
                          }`}
                        >
                          <input
                            type="radio"
                            name="interestedFeature"
                            value={option.id}
                            checked={isSelected}
                            onChange={(e) => updateFormData('interestedFeature', e.target.value)}
                            className="sr-only"
                            disabled={submitting}
                          />
                          {isSelected && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary/100" />
                          )}
                          {option.label}
                        </label>
                      );
                    })}
                  </div>

                  {formData.interestedFeature === 'other' && (
                    <input
                      type="text"
                      value={formData.interestedFeatureOther}
                      onChange={(e) => updateFormData('interestedFeatureOther', e.target.value)}
                      placeholder={t('waitlist.featureOtherPlaceholder')}
                      className="w-full px-4 py-2.5 bg-card border border-border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground/70 text-sm transition-colors"
                      disabled={submitting}
                    />
                  )}
                </div>

                {/* Collapsible Optional Feedback */}
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowFeedbackSection(!showFeedbackSection)}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ChevronDown className={`w-4 h-4 transition-transform ${showFeedbackSection ? 'rotate-180' : ''}`} />
                    {showFeedbackSection ? t('waitlist.hideFeedback') : t('waitlist.shareFeedback')}
                  </button>

                  {showFeedbackSection && (
                    <div className="grid md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border/50">
                      <div className="space-y-2">
                        <label htmlFor="wl-challenge" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                          {t('waitlist.challengeLabel')}
                        </label>
                        <textarea
                          id="wl-challenge"
                          value={formData.biggestChallenge}
                          onChange={(e) => updateFormData('biggestChallenge', e.target.value)}
                          placeholder={t('waitlist.challengePlaceholder')}
                          rows={2}
                          className="w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary focus:bg-card text-foreground placeholder:text-muted-foreground/70 text-sm transition-all resize-none"
                          disabled={submitting}
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="wl-feature-req" className="block text-xs font-medium tracking-wider uppercase text-muted-foreground">
                          {t('waitlist.featureRequestLabel')}
                        </label>
                        <textarea
                          id="wl-feature-req"
                          value={formData.featureRequest}
                          onChange={(e) => updateFormData('featureRequest', e.target.value)}
                          placeholder={t('waitlist.featureRequestPlaceholder')}
                          rows={2}
                          className="w-full px-3 py-2 bg-secondary border border-border rounded-lg focus:border-primary focus:ring-1 focus:ring-primary focus:bg-card text-foreground placeholder:text-muted-foreground/70 text-sm transition-all resize-none"
                          disabled={submitting}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    <X className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full group px-8 py-3.5 bg-primary hover:bg-primary/90 text-white font-medium tracking-wide uppercase text-sm rounded-lg shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <span className="flex items-center justify-center gap-3">
                      {submitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {t('waitlist.submitting')}
                        </>
                      ) : (
                        <>
                          {t('waitlist.submitButton')}
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </div>

              <p className="text-center text-xs text-muted-foreground/70 mt-6">
                {t('waitlist.privacyNote')}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
